// DisinfectionsByDepartmentTable.tsx
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { CloudDataGrid } from "components/modules/CloudDataGrid";
import { MiniDisinfectionModes } from "components/modules/tableCells/MiniDisinfectionModes";
import { DisinfectionFilter, DisinfectionModes } from "gql/graphql";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { getFormattedDate } from "utils/utilFunctions";
import { useDDFilter } from "../../modules/disinfectionFilter/DisinfectionFilterPanel";
import { DISINFECTIONS_BY_ROOM_QUERY } from "./DisinfectionsByRoomTable";

interface Props {
  hiddenFilter?: DisinfectionFilter;
  pageSize?: number;
  enableDDFilter?: boolean;
}

export const DisinfectionsByDepartmentTable = ({
  hiddenFilter,
  pageSize,
  enableDDFilter = false,
}: Props) => {
  const { orgId } = useParams();
  const [filterParams, setFilterParams] = useDDFilter();

  // We reuse this query as underlying data is the same.
  // It will not be triggered twice, apollo will cache it clientside.
  const { data, loading } = useQueryWithSnack(DISINFECTIONS_BY_ROOM_QUERY, {
    variables: {
      filter: {
        ...(enableDDFilter ? filterParams : {}),
        ...hiddenFilter,
        ...(orgId ? { organizationIds: [orgId] } : {}),
      },
    },
  });

  // Restructure data to group by department
  const departmentData = useMemo(() => {
    if (!data?.disinfectionRooms) return [];

    const departments = data.disinfectionRooms.reduce((acc: any, room: any) => {
      const dept = room.department || "Unassigned";
      if (!acc[dept]) {
        acc[dept] = {
          department: dept,
          roomCount: 0,
          autonomous: 0,
          remoteControlled: 0,
          stationary: 0,
          complete: 0,
          incomplete: 0,
          interruptions: 0,
          rooms: [],
          lastDateDisinfected: room.lastDateDisinfected,
        };
      }

      acc[dept].roomCount++;
      acc[dept].autonomous += room.autonomous;
      acc[dept].remoteControlled += room.remoteControlled;
      acc[dept].stationary += room.stationary;
      acc[dept].complete += room.complete;
      acc[dept].incomplete += room.incomplete;
      acc[dept].interruptions += room.interruptions;
      acc[dept].rooms.push(room.room);

      if (
        new Date(room.lastDateDisinfected) >
        new Date(acc[dept].lastDateDisinfected)
      ) {
        acc[dept].lastDateDisinfected = room.lastDateDisinfected;
      }

      return acc;
    }, {});

    // Convert to array and calculate totals
    const departmentArray = Object.values(departments).filter(
      (dept: any) => dept.department !== "Unassigned",
    );
    const totalDisinfections = departmentArray.reduce(
      (sum: number, dept: any) =>
        sum +
        (dept.autonomous ?? 0) +
        (dept.remoteControlled ?? 0) +
        (dept.stationary ?? 0),
      0,
    );

    if (totalDisinfections === 0) {
      return [];
    }
    return departmentArray.map((dept: any) => ({
      ...dept,
      totalDisinfections:
        (dept.autonomous ?? 0) +
        (dept.remoteControlled ?? 0) +
        (dept.stationary ?? 0),
      percentOfAll: Number(
        (
          (((dept.autonomous ?? 0) +
            (dept.remoteControlled ?? 0) +
            (dept.stationary ?? 0)) /
            totalDisinfections) *
          100
        ).toFixed(1),
      ),
    }));
  }, [data]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "department",
        flex: 1,
        minWidth: 200,
        maxWidth: 250,
        headerName: "Department",
        renderCell: (params) => (
          <Typography fontWeight={500}>
            {params.value || "Unassigned"}
          </Typography>
        ),
      },
      {
        field: "rooms",
        headerName: "Rooms",
        flex: 1,
        minWidth: 200,
        maxWidth: 250,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              flexWrap: "wrap",
              alignItems: "center",
              width: "100%",
              height: "100%",
              overflowY: "auto",
            }}
          >
            {params.value.map((room: string) => (
              <Chip key={room} label={room} variant="outlined" size="small" />
            ))}
          </Box>
        ),
      },
      {
        field: "lastDateDisinfected",
        flex: 1,
        maxWidth: 150,
        align: "right",
        headerAlign: "center",
        headerName: "Last disinfected on",
        renderCell: (params) => getFormattedDate(new Date(params.value)),
      },
      {
        field: "interruptions",
        headerName: "Interruptions",
        type: "number",
        maxWidth: 150,
        align: "center",
        flex: 1,
        valueGetter: (params: GridRenderCellParams) => {
          const interruptions = Number(params.row.interruptions) || 0;
          return interruptions;
        },
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return (
            <Typography sx={{ color: theme.palette.warning.main }}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "completionRate",
        headerName: "% Completion Rate",
        type: "number",
        maxWidth: 150,
        flex: 1,
        valueGetter: (params: GridRenderCellParams) => {
          const complete = Number(params.row.complete) || 0;
          const incomplete = Number(params.row.incomplete) || 0;
          const total = complete + incomplete;

          return Number(((complete / (total ? total : 1)) * 100).toFixed(1));
        },
        renderCell: (params: GridRenderCellParams) => (
          <Typography>{params.value}%</Typography>
        ),
      },
      {
        field: "incomplete",
        headerName: "Incomplete",
        align: "right",
        headerAlign: "right",
        flex: 1,
        maxWidth: 100,
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  departments: [params.row.department],
                  disinfectionStatus: "InComplete",
                });
              }}
              sx={{ color: theme.palette.error.main }}
            >
              {params.value}
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.error.main }}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "complete",
        headerName: "Complete",
        align: "left",
        headerAlign: "left",
        flex: 1,
        maxWidth: 100,
        renderCell: (params: GridRenderCellParams) => {
          const theme = useTheme();
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  departments: [params.row.department],
                  disinfectionStatus: "Complete",
                });
              }}
              sx={{ color: theme.palette.success.main }}
            >
              {params.value}
            </Typography>
          ) : (
            <Typography sx={{ color: theme.palette.success.main }}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "total",
        headerName: "Total",
        align: "right",
        headerAlign: "right",
        flex: 1,
        maxWidth: 100,
        valueGetter(params) {
          return (
            (params.row.autonomous ?? 0) +
            (params.row.remoteControlled ?? 0) +
            (params.row.stationary ?? 0)
          );
        },
        renderCell: (params: GridRenderCellParams) => {
          return params.value > 0 ? (
            <Typography
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setFilterParams({
                  ...filterParams,
                  departments: [params.row.department],
                });
              }}
              sx={{ color: "black" }}
            >
              {params.value}
            </Typography>
          ) : (
            <Typography sx={{ color: "black" }}>{params.value}</Typography>
          );
        },
      },
      {
        field: "% Of All",
        headerName: "% Of All",
        align: "left",
        headerAlign: "left",
        flex: 1,
        maxWidth: 200,
        valueGetter(params) {
          const grandTotal =
            params.api.getRowModels().size > 0
              ? Array.from(params.api.getRowModels().values()).reduce(
                  (sum: number, row: any) =>
                    sum +
                    ((row.autonomous ?? 0) +
                      (row.remoteControlled ?? 0) +
                      (row.stationary ?? 0)),
                  0,
                )
              : 0;

          if (grandTotal === 0) {
            return 0;
          }
          return Number(
            (
              (((params.row.autonomous ?? 0) +
                (params.row.remoteControlled ?? 0) +
                (params.row.stationary ?? 0)) /
                grandTotal) *
              100
            ).toFixed(1),
          );
        },
        renderCell: (params) => (
          <Typography>
            {params.value ? Number(params.value.toFixed(1)) : 0}%
          </Typography>
        ),
      },
      {
        field: "graph",
        headerName: "",
        renderCell: MiniDisinfectionModes,
        width: 76,
        sortable: false,
      },
      {
        field: "autonomous",
        headerName: "Predefined",
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        renderCell: (params) => {
          const total = params.row.totalDisinfections;
          const percentage =
            total > 0
              ? Number((((params.value ?? 0) / total) * 100).toFixed(1))
              : 0;
          return (
            <>
              {params.value > 0 ? (
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterParams({
                      ...filterParams,
                      departments: [params.row.department],
                      disinfectionModes: [DisinfectionModes.Autonomous],
                    });
                  }}
                  sx={{ color: "black" }}
                >
                  {params.value}
                </Typography>
              ) : (
                <Typography>{params.value ? params.value : 0}</Typography>
              )}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  marginLeft: theme.spacing(1),
                })}
              >
                ({percentage}%)
              </Typography>
            </>
          );
        },
      },
      {
        field: "remoteControlled",
        headerName: "Remote Controlled",
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        renderCell: (params) => {
          const total = params.row.totalDisinfections;
          const percentage =
            total > 0
              ? Number((((params.value ?? 0) / total) * 100).toFixed(1))
              : 0;
          return (
            <>
              {params.value > 0 ? (
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterParams({
                      ...filterParams,
                      departments: [params.row.department],
                      disinfectionModes: [DisinfectionModes.RemoteControlled],
                    });
                  }}
                  sx={{ color: "black" }}
                >
                  {params.value}
                </Typography>
              ) : (
                <Typography>{params.value ? params.value : 0}</Typography>
              )}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  marginLeft: theme.spacing(1),
                })}
              >
                ({percentage}%)
              </Typography>
            </>
          );
        },
      },
      {
        field: "stationary",
        headerName: "Stationary",
        align: "right",
        headerAlign: "right",
        minWidth: 80,
        renderCell: (params) => {
          const total = params.row.totalDisinfections;
          const percentage =
            total > 0
              ? Number((((params.value ?? 0) / total) * 100).toFixed(1))
              : 0;
          return (
            <>
              {params.value > 0 ? (
                <Typography
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterParams({
                      ...filterParams,
                      departments: [params.row.department],
                      disinfectionModes: [DisinfectionModes.Stationary],
                    });
                  }}
                  sx={{ color: "black" }}
                >
                  {params.value}
                </Typography>
              ) : (
                <Typography>{params.value ? params.value : 0}</Typography>
              )}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  marginLeft: theme.spacing(1),
                })}
              >
                ({percentage}%)
              </Typography>
            </>
          );
        },
      },
    ],
    [filterParams, setFilterParams],
  );

  return (
    <WidgetPaper>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <WidgetHeader
          label={
            <FormattedMessage
              id="disinfection-by-department"
              defaultMessage="Disinfections per Department"
            />
          }
          description="Number of disinfection cycles per department"
        />
      </Box>
      <CloudDataGrid
        rows={departmentData}
        columns={columns}
        loading={loading}
        getRowId={(option) => option.department}
        pageSize={pageSize || 100}
      />
    </WidgetPaper>
  );
};
